import { ArrowRight, Bot, ExternalLink } from 'lucide-react';
import { DocBreadcrumbs } from '../components/DocBreadcrumbs';
import { DocPagination } from '../components/DocPagination';
import { PagePurpose } from '../components/PagePurpose';
import { useCatalog } from '../useCatalog';
import { EXPLORER_URL } from '../config';
import type { CatalogProcess } from '@/lib/sb-api-client';

const palette = {
  accent: '#0F6B7C',
  border: '#D4E2F4',
  tint: '#F4F7FC',
};

// Display order + human labels for the registry's `category` values. Any
// category not listed here still renders (labelled by its raw key).
const categoryMeta: { key: string; label: string; blurb: string }[] = [
  {
    key: 'accessibility',
    label: 'Accessibility & travel time',
    blurb: 'Reachability heatmaps, isochrones, catchments, and travel-time matrices.',
  },
  {
    key: 'cartography',
    label: 'Cartography & map export',
    blurb: 'Branded, PyQGIS-rendered maps and batch PDF exports.',
  },
  {
    key: 'odin',
    label: 'ODiN travel-survey analysis',
    blurb: 'Modal split, comparisons, mobility profiles, and desire-line diagrams from ODiN.',
  },
];

function ClaudeMarker() {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: 'rgba(15, 107, 124, 0.1)', color: palette.accent }}
    >
      <Bot size={13} aria-hidden />
      Available to Claude
    </span>
  );
}

function ProcessCard({ process }: { process: CatalogProcess }) {
  return (
    <div
      className="flex flex-col rounded-2xl border p-5"
      style={{ borderColor: palette.border, backgroundColor: palette.tint }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-lg font-semibold text-sb-black">{process.title}</h3>
        {process.mcp_name && <ClaudeMarker />}
      </div>
      <code className="mt-1 block text-xs font-mono text-gray-500">{process.id}</code>
      <p className="mt-2 flex-1 text-sm text-sb-dark-grey">{process.description}</p>
      {process.methodology && (
        <p className="mt-3 text-xs text-gray-500">
          <span className="font-semibold uppercase tracking-wide">Method:</span> {process.methodology}
        </p>
      )}
      <a
        href={EXPLORER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold"
        style={{ color: palette.accent }}
      >
        Try in the explorer <ArrowRight size={15} aria-hidden />
      </a>
    </div>
  );
}

export function MethodologiesPage() {
  const { loading, processesError, processCount, mcpToolCount, processesByCategory } = useCatalog();

  // Keep the configured categories in order, then append any extra categories
  // the registry returns that we haven't given a label yet.
  const knownKeys = new Set(categoryMeta.map((meta) => meta.key));
  const extraKeys = Object.keys(processesByCategory).filter((key) => !knownKeys.has(key));
  const orderedCategories = [
    ...categoryMeta,
    ...extraKeys.map((key) => ({ key, label: key, blurb: '' })),
  ];

  return (
    <div className="space-y-8">
      <DocBreadcrumbs currentTitle="Methodologies" />

      <PagePurpose
        title="Methodologies — every method, live from the registry"
        audience="Data analysts, project managers, and developers integrating SB data"
        description="Each SB methodology is encoded once as an OGC API process. This page lists them live from the registry — so what you see here is exactly what's callable right now."
        badge="Live"
      />

      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: palette.accent }}>
          OGC API processes
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-sb-black">Analysis methods</h1>
        <p className="mt-4 max-w-3xl text-base text-sb-dark-grey">
          Every method below is a standardised OGC API process at{' '}
          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm font-mono">/oapi/processes/</code>.
          Encode a method once and it's available as a self-service tool, inside QGIS, to Claude, and in
          the explorer — no extra integration per surface.
        </p>
        <p className="mt-3 max-w-3xl text-sm text-sb-dark-grey">
          {processCount === null
            ? 'Live figures temporarily unavailable — the count below reads directly from the registry.'
            : `${processCount} method${processCount === 1 ? '' : 's'} live today` +
              (mcpToolCount !== null
                ? `, of which ${mcpToolCount} are wired as Claude tools.`
                : '.')}
        </p>
        <div
          className="mt-4 rounded-2xl border px-4 py-3 text-sm"
          style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB', color: '#92400E' }}
        >
          Not every method is exposed as a Claude tool — by design. Claude works numbers-only, so
          image and cartography methods stay API- and tool-only.
        </div>
      </section>

      {loading && (
        <section
          className="rounded-3xl border bg-white p-8 text-center text-sm text-sb-dark-grey shadow-sm"
          style={{ borderColor: palette.border }}
        >
          Loading methods from the registry…
        </section>
      )}

      {!loading && processesError && (
        <section
          className="rounded-3xl border p-8 text-center text-sm font-semibold shadow-sm"
          style={{ borderColor: '#FCD34D', backgroundColor: '#FFFBEB', color: '#92400E' }}
        >
          Live figures temporarily unavailable — could not reach the registry. Please try again shortly.
        </section>
      )}

      {!loading &&
        !processesError &&
        orderedCategories.map((category) => {
          const processes = processesByCategory[category.key];
          if (!processes || processes.length === 0) return null;
          return (
            <section
              key={category.key}
              className="rounded-3xl border bg-white p-6 shadow-sm"
              style={{ borderColor: palette.border }}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-2xl font-semibold text-sb-black">{category.label}</h2>
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {processes.length} method{processes.length === 1 ? '' : 's'}
                </span>
              </div>
              {category.blurb && <p className="mt-1 text-sm text-sb-dark-grey">{category.blurb}</p>}
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {processes.map((process) => (
                  <ProcessCard key={process.id} process={process} />
                ))}
              </div>
            </section>
          );
        })}

      {/* SB-elaborated methodology (authored — links to the real rollout) */}
      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: palette.accent }}>
          Documented data-team workflow
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-sb-black">Verstedelijking (urbanisation)</h2>
        <p className="mt-3 max-w-3xl text-base text-sb-dark-grey">
          Roland's urbanisation methodology turns national scenario data into proximity/urbanisation
          indicators per zone. It's SB-elaborated IP built on top of open sources — see it applied
          end-to-end in the SB470 rollout.
        </p>
        <a
          href="/documentation/showcase"
          className="mt-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold text-sb-black transition hover:bg-sb-black hover:text-white"
          style={{ borderColor: palette.border }}
        >
          See it applied: SB470 Project Showcase <ArrowRight size={15} aria-hidden />
        </a>
      </section>

      {/* Try the data live */}
      <section
        className="rounded-3xl border p-6 shadow-sm"
        style={{ borderColor: palette.accent, backgroundColor: palette.tint }}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-sb-black">Try the data live</h2>
            <p className="mt-1 text-sm text-sb-dark-grey">Run any of these methods against real data in the explorer.</p>
          </div>
          <a
            href={EXPLORER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
            style={{ backgroundColor: palette.accent }}
          >
            Open the explorer <ExternalLink size={15} aria-hidden />
          </a>
        </div>
      </section>

      <DocPagination currentPageId="methodologies" />
    </div>
  );
}
